import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Loader } from '@/components/Loader/Loader'
import { useAppContext } from '@/hooks'
import { MRDiffDrawer } from '@/pages/User/pages/MergeRequests/components/MRDiffDrawer'
import { changeToMrStructure } from '@/pages/User/pages/MergeRequests/mapMergeRequest'
import { MrStructure } from '@/pages/User/pages/MergeRequests/types'
import { useCherryPickMutation, usePreviewCherryPickMutation } from '@/rtkApi'
import { BranchCherryPickRequestDto } from '@/rtkApi/mddApiSliceGeneratedV2'
import {
	getApiErrorDescription,
	logErrorAndNotifyWithToast,
} from '@/utils/errorHandling'

import {
	CherryPickElement,
	CherryPickExecuteBody,
	cherryPickExecuteRequest,
	cherryPickFieldForPath,
	CherryPickResolutions,
	cherryPickResolutionValue,
	cherryPickSelectionsComplete,
	hasCherryPickConflicts,
	isCherryPickBlocked,
	isTransferredElement,
	splitCherryPickPreview,
} from '../cherryPick'
import { RebaseChoice } from '../rebaseBranch'

type CherryPickDialogProps = {
	isOpen: boolean
	onClose: () => void
	onFinished: () => void
	sourceBranchId: number
	sourceBranchName: string
	structureIds: number[]
	targetBranchId: number
	targetBranchName: string
}

type Phase = 'loading' | 'blocked' | 'dependencies' | 'confirm' | 'result'

const toStructure = (element: CherryPickElement): MrStructure =>
	changeToMrStructure({
		changeType: 'MODIFIED',
		code: element.code,
		conflicts: element.conflicts,
		name: element.name,
		structureId: element.structureId,
	})

const elementLabel = (element: CherryPickElement) =>
	element.name || element.code || String(element.structureId ?? '')

export const CherryPickDialog = ({
	isOpen,
	onClose,
	onFinished,
	sourceBranchId,
	sourceBranchName,
	structureIds,
	targetBranchId,
	targetBranchName,
}: CherryPickDialogProps) => {
	const { t } = useAppContext()
	const [previewCherryPick] = usePreviewCherryPickMutation()
	const [cherryPick] = useCherryPickMutation()
	const [phase, setPhase] = useState<Phase>('loading')
	const [loading, setLoading] = useState(false)
	const [blockedMessage, setBlockedMessage] = useState('')
	const [blockedRequests, setBlockedRequests] = useState<
		{ id?: number; title?: string }[]
	>([])
	const [kept, setKept] = useState<CherryPickElement[]>([])
	const [skipped, setSkipped] = useState<CherryPickElement[]>([])
	const [missing, setMissing] = useState<CherryPickElement[]>([])
	const [structures, setStructures] = useState<MrStructure[]>([])
	const [selected, setSelected] = useState<CherryPickResolutions>({})
	const [openStructureId, setOpenStructureId] = useState<string | null>(null)
	const [continueWithMissing, setContinueWithMissing] = useState(false)
	const [transferredCount, setTransferredCount] = useState(0)
	const [outcomeLines, setOutcomeLines] = useState<
		{ key: string; label: string; message: string }[]
	>([])
	const started = useRef(false)
	const openRef = useRef(isOpen)
	openRef.current = isOpen

	const conflicts = kept.filter(hasCherryPickConflicts)
	const openStructure =
		structures.find((structure) => structure.key === openStructureId) ?? null
	const canApply =
		kept.length > 0 && cherryPickSelectionsComplete(conflicts, selected) && !loading

	const reset = () => {
		setPhase('loading')
		setLoading(false)
		setBlockedMessage('')
		setBlockedRequests([])
		setKept([])
		setSkipped([])
		setMissing([])
		setStructures([])
		setSelected({})
		setOpenStructureId(null)
		setContinueWithMissing(false)
		setTransferredCount(0)
		setOutcomeLines([])
	}

	const showBlocked = useCallback(
		(
			message: string | undefined,
			requests: { id?: number; title?: string }[] | undefined,
		) => {
			setBlockedMessage(message || t('BRANCHES_CHERRY_PICK_BLOCKED'))
			setBlockedRequests(requests ?? [])
			setPhase('blocked')
		},
		[t],
	)

	const showOutcome = useCallback(
		(executed: CherryPickElement[], leftOut: CherryPickElement[]) => {
			const lines = [...leftOut, ...executed.filter((element) => !isTransferredElement(element))]
			setTransferredCount(executed.filter(isTransferredElement).length)
			setOutcomeLines(
				lines.map((element, index) => ({
					key: `${element.structureId ?? 'row'}-${index}`,
					label: elementLabel(element),
					message: element.message ?? '',
				})),
			)
			setPhase('result')
			onFinished()
		},
		[onFinished],
	)

	const execute = useCallback(
		async (
			elements: CherryPickElement[],
			allowMissing: boolean,
			resolutions: CherryPickResolutions,
			leftOut: CherryPickElement[],
		) => {
			setLoading(true)
			try {
				const body: CherryPickExecuteBody = cherryPickExecuteRequest({
					continueWithMissingDependencies: allowMissing,
					elements,
					resolutions,
					sourceBranchId,
				})
				const next = await cherryPick({
					branchId: targetBranchId,
					branchCherryPickRequestDto: body as BranchCherryPickRequestDto,
				}).unwrap()
				if (!openRef.current) return
				if (isCherryPickBlocked(next)) {
					showBlocked(next.message, next.blockingMergeRequests)
					return
				}
				showOutcome(next.elements ?? [], leftOut)
			} catch (error) {
				if (!openRef.current) return
				setPhase((current) => (current === 'loading' ? 'confirm' : current))
				logErrorAndNotifyWithToast({
					context: { operation: 'CherryPickDialog.execute' },
					detail: getApiErrorDescription(error),
					error,
					messageKey: 'BRANCHES_CHERRY_PICK_FAILED',
					t,
				})
			} finally {
				setLoading(false)
			}
		},
		[cherryPick, showBlocked, showOutcome, sourceBranchId, t, targetBranchId],
	)

	useEffect(() => {
		if (!isOpen) {
			started.current = false
			reset()
			return
		}
		if (started.current) return
		started.current = true
		setLoading(true)
		void previewCherryPick({
			branchId: targetBranchId,
			branchCherryPickRequestDto: {
				sourceBranchId,
				structureIds,
			},
		})
			.unwrap()
			.then((next) => {
				if (!openRef.current) return
				if (isCherryPickBlocked(next)) {
					showBlocked(next.message, next.blockingMergeRequests)
					setLoading(false)
					return
				}
				const split = splitCherryPickPreview(next.elements ?? [])
				setKept(split.kept)
				setSkipped(split.skipped)
				setMissing(split.missing)
				setStructures(split.kept.filter(hasCherryPickConflicts).map(toStructure))
				if (split.missing.length > 0) {
					setPhase('dependencies')
					setLoading(false)
					return
				}
				if (split.kept.some(hasCherryPickConflicts) || split.kept.length === 0) {
					setPhase('confirm')
					setLoading(false)
					return
				}
				void execute(split.kept, false, {}, split.skipped)
			})
			.catch((error: unknown) => {
				if (!openRef.current) return
				setLoading(false)
				logErrorAndNotifyWithToast({
					context: { operation: 'CherryPickDialog.preview' },
					detail: getApiErrorDescription(error),
					error,
					messageKey: 'BRANCHES_CHERRY_PICK_FAILED',
					t,
				})
				onClose()
			})
	}, [
		execute,
		isOpen,
		onClose,
		previewCherryPick,
		showBlocked,
		sourceBranchId,
		structureIds,
		t,
		targetBranchId,
	])

	const recordResolution = async (
		structure: MrStructure,
		enginePath: string,
		choice: RebaseChoice,
		customValue?: unknown,
	) => {
		const element = kept.find((item) => String(item.structureId) === structure.key)
		const field = element ? cherryPickFieldForPath(element, enginePath) : undefined
		if (!element?.structureId || !field?.fieldPath) return
		const value = cherryPickResolutionValue(field, choice, customValue)
		setSelected((current) => ({
			...current,
			[String(element.structureId)]: {
				...current[String(element.structureId)],
				[field.fieldPath as string]: value,
			},
		}))
	}

	const drop = (structureId: number) => {
		setKept((current) =>
			current.filter((element) => element.structureId !== structureId),
		)
		setStructures((current) =>
			current.filter((structure) => structure.key !== String(structureId)),
		)
		setSelected((current) => {
			const next = { ...current }
			delete next[String(structureId)]
			return next
		})
		if (openStructureId === String(structureId)) setOpenStructureId(null)
	}

	const continueDespiteMissing = () => {
		setContinueWithMissing(true)
		if (kept.some(hasCherryPickConflicts)) {
			setPhase('confirm')
			return
		}
		void execute(kept, true, {}, skipped)
	}

	return (
		<>
			<Dialog
				open={isOpen && !openStructure}
				onClose={onClose}
				maxWidth="sm"
				fullWidth
				data-testid="branch-cherry-pick-dialog"
				slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
			>
				<DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#1F4954' }}>
					{t('BRANCHES_CHERRY_PICK')}
				</DialogTitle>
				<DialogContent>
					{loading || phase === 'loading' ? <Loader loaded={false} /> : null}
					{phase === 'blocked' ? (
						<Box>
							<Typography sx={{ fontSize: 14, mb: 1.5 }}>{blockedMessage}</Typography>
							{blockedRequests.map((request) => (
								<Typography
									key={request.id}
									sx={{ fontSize: 13, color: '#3D3D3D' }}
								>
									{request.title || String(request.id ?? '')}
								</Typography>
							))}
						</Box>
					) : null}
					{phase === 'dependencies' ? (
						<Box sx={{ display: 'grid', gap: 1 }}>
							<Typography sx={{ fontSize: 14 }}>
								{t('BRANCHES_CHERRY_PICK_MISSING')}
							</Typography>
							{missing.map((element) => (
								<Typography
									key={element.structureId}
									sx={{ fontSize: 13, color: '#3D3D3D' }}
								>
									{t('BRANCHES_CHERRY_PICK_MISSING_ITEM', [
										elementLabel(element),
										(element.missingDependencies ?? []).join(', '),
									])}
								</Typography>
							))}
						</Box>
					) : null}
					{phase === 'confirm' ? (
						<Box sx={{ display: 'grid', gap: 1 }}>
							{conflicts.length > 0 ? (
								<Typography sx={{ fontSize: 14 }}>
									{t('BRANCHES_CHERRY_PICK_CONFLICTS')}
								</Typography>
							) : null}
							{kept.length === 0 ? (
								<Typography sx={{ fontSize: 14 }}>
									{t('BRANCHES_CHERRY_PICK_NOTHING')}
								</Typography>
							) : (
								<Typography sx={{ fontSize: 13, color: '#52666C' }}>
									{t('BRANCHES_CHERRY_PICK_INCLUDED', [String(kept.length)])}
								</Typography>
							)}
							{conflicts.map((element) => {
								const resolved = cherryPickSelectionsComplete([element], selected)
								return (
									<Box
										key={element.structureId}
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: 1,
										}}
									>
										<Typography sx={{ fontSize: 13 }}>
											{elementLabel(element)}
										</Typography>
										<Box sx={{ display: 'flex', gap: 1 }}>
											<Button
												size="small"
												onClick={() =>
													setOpenStructureId(String(element.structureId ?? ''))
												}
												sx={{ textTransform: 'none' }}
											>
												{resolved
													? t('BRANCHES_CHERRY_PICK_RESOLVED')
													: t('BRANCHES_CHERRY_PICK_RESOLVE')}
											</Button>
											<Button
												size="small"
												onClick={() =>
													element.structureId != null && drop(element.structureId)
												}
												sx={{ textTransform: 'none' }}
												data-testid={`branch-cherry-pick-drop-${element.structureId}`}
											>
												{t('BRANCHES_CHERRY_PICK_DROP')}
											</Button>
										</Box>
									</Box>
								)
							})}
							{skipped.map((element) => (
								<Typography
									key={element.structureId}
									sx={{ fontSize: 13, color: '#6A6A6A' }}
								>
									{element.message
										? t('BRANCHES_CHERRY_PICK_SKIPPED', [
												elementLabel(element),
												element.message,
											])
										: elementLabel(element)}
								</Typography>
							))}
						</Box>
					) : null}
					{phase === 'result' ? (
						<Box sx={{ display: 'grid', gap: 1 }}>
							<Typography sx={{ fontSize: 14 }} data-testid="branch-cherry-pick-result">
								{transferredCount === 1
									? t('BRANCHES_CHERRY_PICK_TRANSFERRED_ONE')
									: t('BRANCHES_CHERRY_PICK_TRANSFERRED', [
											String(transferredCount),
										])}
							</Typography>
							{outcomeLines.map((line) => (
								<Typography key={line.key} sx={{ fontSize: 13, color: '#3D3D3D' }}>
									{line.message
										? t('BRANCHES_CHERRY_PICK_SKIPPED', [line.label, line.message])
										: line.label}
								</Typography>
							))}
						</Box>
					) : null}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5 }}>
					{phase === 'dependencies' ? (
						<Button
							variant="contained"
							disableElevation
							disabled={loading}
							onClick={continueDespiteMissing}
							sx={{ textTransform: 'none' }}
						>
							{t('BRANCHES_CHERRY_PICK_CONTINUE')}
						</Button>
					) : null}
					{phase === 'confirm' ? (
						<Button
							variant="contained"
							disableElevation
							disabled={!canApply}
							onClick={() =>
								void execute(kept, continueWithMissing, selected, skipped)
							}
							sx={{ textTransform: 'none' }}
							data-testid="branch-cherry-pick-apply"
						>
							{t('BRANCHES_CHERRY_PICK_APPLY')}
						</Button>
					) : null}
					<Button onClick={onClose} sx={{ textTransform: 'none' }}>
						{phase === 'result' || phase === 'blocked' ? t('CLOSE') : t('CANCEL')}
					</Button>
				</DialogActions>
			</Dialog>
			<MRDiffDrawer
				open={Boolean(openStructure)}
				structure={openStructure}
				sourceBranchName={sourceBranchName}
				targetBranchName={targetBranchName}
				onClose={() => setOpenStructureId(null)}
				onResolve={recordResolution}
			/>
		</>
	)
}
